/* eslint-disable no-nested-ternary */
import { useAuth } from '@context';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { UserRoleService } from '../../services';
import { isStarterChecklistOpenAtom, sidebarCollapsedAtom } from '../../store/jotai';

export interface StarterChecklistDto {
  hasCompletedBusinessProfile: boolean;
  hasSetUpFinancialInformation: boolean;
  hasCreatedSku: boolean;
  hasCreatedSupplierMaster: boolean;
  hasCreatedCustomer: boolean;
  hasSearchedForOrOnboardedWarehouse: boolean;
  hasEngagedWithWarehouse: boolean;
  hasContractedWarehouse: boolean;
  hasStockInWarehouse: boolean;
  hasCreatedInboundShippingNote: boolean;
  hasCreatedOutboundShippingNote: boolean;
  hasCreatedWarehouseProfile: boolean;
  hasOnboardedCustomers: boolean;
  hasContractedCustomer: boolean;
  hasReceivedInboundShipment: boolean;
}

interface ChecklistStepProps {
  isComplete: boolean;
  displayText: string;
  explainerText?: string;
  linkToAction?: string;
  linkToTutorial?: string;
}

function ChecklistStep({
  isComplete,
  displayText,
  explainerText = '',
  linkToAction = '',
  linkToTutorial = '',
}: ChecklistStepProps) {
  return (
    <li className='flex items-start gap-3 py-2'>
      {isComplete ? (
        <>
          <CheckCircleIcon
            className='h-5 w-5 flex-none text-green-500 mt-0.5'
            aria-hidden='true'
          />
          <span className='text-sm font-inter text-gray-400 line-through'>
            {displayText}
          </span>
        </>
      ) : (
        <>
          <div className='h-5 w-5 flex-none rounded-full border-2 border-gray-300 mt-0.5' />
          <div className='flex flex-col'>
            <a
              href={linkToAction ? `/${linkToAction}` : '#'}
              className='group flex items-center text-sm font-medium font-inter text-gray-700 hover:text-primary transition-colors'
            >
              {displayText}
              <ChevronRightIcon
                className='h-4 w-4 ml-1 text-gray-400 group-hover:text-primary transition-colors'
                aria-hidden='true'
              />
            </a>
            {explainerText && (
              <span className='text-xs font-inter text-gray-500 mt-0.5'>
                {explainerText}
              </span>
            )}
            {linkToTutorial && (
              <a
                href={linkToTutorial}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center text-xs font-inter text-primary hover:underline mt-1'
              >
                <img
                  src='/images/vimeo_icon.jpg'
                  alt='vimeo'
                  className='h-4 w-4 mr-1.5 rounded'
                />
                Watch tutorial
              </a>
            )}
          </div>
        </>
      )}
    </li>
  );
}

function ProgressBar({
  completionPercentage,
  completedSteps,
  totalSteps,
}: {
  completionPercentage: number;
  completedSteps: number;
  totalSteps: number;
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex justify-between items-center'>
        <span className='text-xs font-inter text-gray-500'>
          {completedSteps} of {totalSteps} completed
        </span>
        <span className='text-xs font-inter font-medium text-gray-700'>
          {completionPercentage.toFixed(0)}%
        </span>
      </div>
      <div className='overflow-hidden rounded-full bg-gray-100 h-2'>
        <div
          style={{ width: `${completionPercentage.toFixed(1)}%` }}
          className='h-full rounded-full bg-primary transition-all duration-300'
        />
      </div>
    </div>
  );
}

interface StarterChecklistProps {
  starterChecklist: StarterChecklistDto;
}

export function StarterChecklist({ starterChecklist }: StarterChecklistProps) {
  const { user } = useAuth();
  const { isWarehouseClient, isWarehouseManager } = UserRoleService();
  const [isStarterChecklistOpen, setIsStarterChecklistOpen] = useAtom(
    isStarterChecklistOpenAtom
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  const isFinanceAdmin = user?.roles?.includes('FinanceAdmin');

  const getRelevantSteps = (): boolean[] => {
    const relevantSteps = !starterChecklist
      ? []
      : isWarehouseClient(user)
      ? [
          starterChecklist.hasCompletedBusinessProfile,
          starterChecklist.hasCreatedSku,
          starterChecklist.hasCreatedSupplierMaster,
          starterChecklist.hasCreatedCustomer,
          starterChecklist.hasEngagedWithWarehouse,
          starterChecklist.hasContractedWarehouse,
          starterChecklist.hasStockInWarehouse,
          starterChecklist.hasCreatedInboundShippingNote,
          starterChecklist.hasCreatedOutboundShippingNote,
        ]
      : isWarehouseManager(user)
      ? [
          starterChecklist.hasCompletedBusinessProfile,
          starterChecklist.hasCreatedWarehouseProfile,
          starterChecklist.hasContractedCustomer,
        ]
      : [];

    if (isFinanceAdmin) {
      relevantSteps.push(
        starterChecklist.hasSetUpFinancialInformation ?? false
      );
    }

    return relevantSteps;
  };

  const isComplete = () => {
    return getRelevantSteps().every((step) => !!step);
  };

  useEffect(() => {
    function updateCompletionPercentage() {
      const total = getRelevantSteps().length;
      const completed = getRelevantSteps().filter(
        (step: boolean) => !!step
      ).length;

      const percentageValue = (100 * completed) / total;
      setCompletionPercentage(percentageValue);
      setCompletedSteps(completed);
      setTotalSteps(total);
    }
    updateCompletionPercentage();
  });

  if (isComplete() || !starterChecklist) {
    return null;
  }

  return (
    <div
      id='checklist'
      className={`fixed left-0 bottom-0 z-starterChecklist bg-white rounded-tr-lg shadow-md border border-gray-200 transition-all duration-300 ease-in-out ${
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      } w-64`}
    >
      {sidebarCollapsed ? (
        /* Collapsed: just show progress as a compact indicator */
        <button
          type='button'
          onClick={() => {
            setSidebarCollapsed(false);
            setIsStarterChecklistOpen(true);
          }}
          className='w-full flex flex-col items-center justify-center p-3 hover:bg-gray-50 transition-colors'
          title={`Starter Checklist: ${completedSteps} of ${totalSteps} completed`}
        >
          <div className='relative h-8 w-8'>
            <svg className='h-8 w-8 -rotate-90' viewBox='0 0 36 36'>
              <circle
                cx='18' cy='18' r='15.5'
                fill='none' stroke='#e5e7eb' strokeWidth='3'
              />
              <circle
                cx='18' cy='18' r='15.5'
                fill='none' stroke='#0066cb' strokeWidth='3'
                strokeDasharray={`${completionPercentage} 100`}
                strokeLinecap='round'
              />
            </svg>
            <span className='absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700'>
              {completedSteps}/{totalSteps}
            </span>
          </div>
        </button>
      ) : (
        <>
          {/* Header */}
          <button
            type='button'
            onClick={() => setIsStarterChecklistOpen(!isStarterChecklistOpen)}
            className='w-full flex items-center justify-between p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors'
          >
            <h3 className='text-sm font-semibold font-inter text-gray-900'>
              Starter Checklist
            </h3>
            {isStarterChecklistOpen ? (
              <ChevronDownIcon
                className='h-5 w-5 text-gray-400'
                aria-hidden='true'
              />
            ) : (
              <ChevronUpIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
            )}
          </button>

          {/* Progress Bar */}
          <div className='px-4 py-3'>
            <ProgressBar
              completionPercentage={completionPercentage}
              completedSteps={completedSteps}
              totalSteps={totalSteps}
            />
          </div>

          {/* Checklist Items */}
          {isStarterChecklistOpen && (
            <div className='px-4 pb-4'>
              <ul className='divide-y divide-gray-100'>
                {isWarehouseClient(user) && (
                  <>
                    <ChecklistStep
                      isComplete={starterChecklist.hasCompletedBusinessProfile}
                      displayText='Complete Business Profile'
                      linkToAction='profile'
                    />
                    {isFinanceAdmin && (
                      <ChecklistStep
                        isComplete={starterChecklist.hasSetUpFinancialInformation}
                        displayText='Payment Details Setup'
                        linkToAction='profile'
                      />
                    )}
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedSku}
                      displayText='Create a SKU'
                      linkToAction='sku-master'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedSupplierMaster}
                      displayText='Create a Supplier'
                      linkToAction='supplier-master'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedCustomer}
                      displayText='Create a Customer'
                      linkToAction='customer-master'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasEngagedWithWarehouse}
                      displayText='Engage with Warehouse'
                      linkToAction='warehouse-shortlist'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasContractedWarehouse}
                      displayText='Contract a Warehouse'
                      linkToAction='warehouse-shortlist'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedInboundShippingNote}
                      displayText='Create Inbound Shipment'
                      linkToAction='shipments'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasStockInWarehouse}
                      displayText='Have stock in Warehouse'
                      linkToAction='stock'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedOutboundShippingNote}
                      displayText='Create Outbound Shipment'
                      linkToAction='shipments'
                    />
                  </>
                )}
                {isWarehouseManager(user) && (
                  <>
                    <ChecklistStep
                      isComplete={starterChecklist.hasCompletedBusinessProfile}
                      displayText='Complete Business Profile'
                      linkToAction='profile'
                    />
                    {isFinanceAdmin && (
                      <ChecklistStep
                        isComplete={starterChecklist.hasSetUpFinancialInformation}
                        displayText='Payment Details Setup'
                        linkToAction='profile'
                      />
                    )}
                    <ChecklistStep
                      isComplete={starterChecklist.hasCreatedWarehouseProfile}
                      displayText='Create a Warehouse'
                      linkToAction='warehouse-master'
                      linkToTutorial='https://vimeo.com/1068985425'
                    />
                    <ChecklistStep
                      isComplete={starterChecklist.hasContractedCustomer}
                      displayText='Contract Customers'
                      linkToAction='customers'
                    />
                  </>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
